import React from 'react';
import { CircularProgress } from '@mui/material';

export default function Preloader({ message = "Loading...", size = 24, fullScreen = false }) {
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl flex items-center justify-center mx-auto shadow-large animate-bounce-soft">
              <span className="text-white font-bold text-2xl">G</span>
            </div>
            <div className="absolute inset-0 w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-500 rounded-3xl animate-ping opacity-20"></div>
          </div>
          <div className="flex items-center justify-center space-x-3 mb-4">
            <CircularProgress size={size} className="text-primary-600" />
            <span className="text-lg font-semibold text-gray-700">{message}</span>
          </div>
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center space-x-3 p-4">
      <div className="relative">
        <CircularProgress size={size} className="text-primary-600" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse"></div>
        </div>
      </div>
      {message && (
        <span className="text-sm font-medium text-gray-600 animate-pulse-soft">
          {message}
        </span>
      )}
    </div>
  );
}

// Additional loading components for different use cases
export function SkeletonLoader({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className="loading-skeleton h-4 rounded-lg"
          style={{ animationDelay: `${index * 100}ms` }}
        ></div>
      ))}
    </div>
  );
}

export function CardSkeleton({ className = "" }) {
  return (
    <div className={`fintech-card p-6 ${className}`}>
      <div className="flex items-center space-x-4 mb-4">
        <div className="loading-skeleton w-12 h-12 rounded-2xl"></div>
        <div className="flex-1 space-y-2">
          <div className="loading-skeleton h-4 w-24 rounded"></div>
          <div className="loading-skeleton h-3 w-32 rounded"></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="loading-skeleton h-6 w-20 rounded"></div>
        <div className="loading-skeleton h-4 w-32 rounded"></div>
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 5, columns = 4, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-xl border border-gray-100/50 ${className}`}>
      <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-100/50">
        <div className="loading-skeleton h-4 w-32 rounded"></div>
      </div>
      <div className="divide-y divide-gray-100/50">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4">
            <div className="grid grid-cols-4 gap-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div
                  key={colIndex}
                  className="loading-skeleton h-4 rounded"
                  style={{ animationDelay: `${(rowIndex * columns + colIndex) * 50}ms` }}
                ></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PulseLoader({ size = "medium", color = "primary" }) {
  const sizeClasses = {
    small: "w-2 h-2",
    medium: "w-3 h-3",
    large: "w-4 h-4"
  };

  const colorClasses = {
    primary: "bg-primary-500",
    success: "bg-success-500",
    warning: "bg-warning-500",
    danger: "bg-danger-500"
  };

  return (
    <div className="flex space-x-1">
      <div 
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-bounce`}
        style={{ animationDelay: '0ms' }}
      ></div>
      <div 
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-bounce`}
        style={{ animationDelay: '150ms' }}
      ></div>
      <div 
        className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-bounce`}
        style={{ animationDelay: '300ms' }}
      ></div>
    </div>
  );
} 